import { useReducer, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useStagesApi from '../../api/stages/useStagesApi';
import useDraftsApi from '../../api/drafts/useDraftsApi';
import { MULTI_FORM_ACTIONS } from '../../../constants/workflow.constant';
import {
  submitFormData,
  mapStageToForms,
} from '../../utils/formSubmissionUtils';
import { initialState, reducer } from './useMultiFormSubmission.reducer';
import { getFileFieldId } from '../../../utils/fileHelpers';

/**
 * useMultiFormSubmission - Feature hook for multi-form submission
 * Used by both new request and send-back flows.
 *
 * @param {number} stageId - Current stage ID (for progress view - shows forms for this specific stage)
 * @param {number} requestProgressId - Current request progress ID (for existing requests to advance)
 * @param {number} draftId - Optional draft ID to load existing draft data
 * @param {boolean} isRequestActive - Whether the request is active/assigned
 */
const useMultiFormSubmission = ({
  workflowVersionId,
  requestId,
  requestProgressId,
  stageId,
  customRequestName,
  existingFilledForms = {},
  targetStageIds = [],
  draftId = null,
  isRequestActive = false,
} = {}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();
  const { getSubmissionStages, getRequestSubmissionStages } = useStagesApi();
  const { getDraftById } = useDraftsApi();
  const [workflowPaymentConfig, setWorkflowPaymentConfig] = useState({
    paymentProviderId: null,
    paymentCurrency: null,
    paymentEnabled: false,
  });

  // Track staged files from draft, organized by "subformId-fieldId" key
  const [draftStagedFiles, setDraftStagedFiles] = useState({});
  // Restored draft data for additional documents comments and linked studies
  const [
    draftAdditionalDocumentsComments,
    setDraftAdditionalDocumentsComments,
  ] = useState(null);
  const [draftLinkedRequestIds, setDraftLinkedRequestIds] = useState(null);
  // NOTE: draftDeletedFileIds removed - file deletions are now immediate via
  // dedicated endpoints (PATCH /api/v1/drafts/:id/deleted-files or POST /api/endUserFilledForms/files/delete).
  // The deleted_file_ids field on the draft model is deprecated.

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: MULTI_FORM_ACTIONS.SET_LOADING, loading: true });

      try {
        let stages = [];
        let prefillData = {};
        let draftFormData = {};
        const loadedStagedFiles = {};

        // Load draft data if draftId is provided
        if (draftId) {
          const draftData = await getDraftById(draftId);
          if (draftData) {
            // Parse form_data from the draft
            try {
              draftFormData =
                typeof draftData.form_data === 'string'
                  ? JSON.parse(draftData.form_data)
                  : draftData.form_data || {};
            } catch (parseErr) {
              console.error('Error parsing draft form_data:', parseErr);
              draftFormData = {};
            }

            // Organize staged files by subformId-fieldId for easy lookup
            // Process legacy stagedFiles (from old staged_files table)
            if (draftData.stagedFiles && Array.isArray(draftData.stagedFiles)) {
              draftData.stagedFiles.forEach((file) => {
                if (file.subform_id && file.field_id) {
                  const key = file.form_id
                    ? `${file.form_id}-${file.subform_id}-${file.field_id}`
                    : `${file.subform_id}-${file.field_id}`;
                  if (!loadedStagedFiles[key]) {
                    loadedStagedFiles[key] = [];
                  }
                  loadedStagedFiles[key].push({
                    id: file.id,
                    name: file.original_name,
                    size: parseInt(file.file_size, 10) || 0,
                    type: file.mime_type,
                    isStagedFile: true,
                    stagedId: file.id,
                    formId: file.form_id,
                  });
                }
              });
            }

            // Process new file module files (from files/attachments tables)
            // context is a JSON object with frontend metadata (e.g. { field_id, alt_text })
            // field_id/subform_id/form_id are also available as flat properties for backward compat
            if (draftData.files && Array.isArray(draftData.files)) {
              draftData.files.forEach((file) => {
                const fieldId = getFileFieldId(file);
                const subformId = file.subform_id;
                const formId = file.form_id;
                if (subformId && fieldId) {
                  const key = formId
                    ? `${formId}-${subformId}-${fieldId}`
                    : `${subformId}-${fieldId}`;
                  if (!loadedStagedFiles[key]) {
                    loadedStagedFiles[key] = [];
                  }
                  loadedStagedFiles[key].push({
                    id: file.id,
                    name: file.original_name,
                    size: parseInt(file.file_size, 10) || 0,
                    type: file.mime_type,
                    isStagedFile: true,
                    isNewFileModule: true,
                    stagedId: file.id,
                    formId,
                    subformId,
                    fieldId,
                    context: file.context,
                  });
                }
              });
            }

            // Update state with all loaded files (both legacy and new module)
            if (Object.keys(loadedStagedFiles).length > 0) {
              setDraftStagedFiles(loadedStagedFiles);
            }

            // Restore additional documents comments and linked studies from draft
            if (draftData.additional_documents_comments) {
              setDraftAdditionalDocumentsComments(
                draftData.additional_documents_comments
              );
            }
            if (draftData.linked_request_ids) {
              setDraftLinkedRequestIds(draftData.linked_request_ids);
            }

            // NOTE: deleted_file_ids no longer loaded from draft - deletions are now immediate
          }
        }

        if (requestId) {
          const response = await getRequestSubmissionStages(requestId);
          stages = response.stages || [];
          prefillData = response.filledForms || {};
          setWorkflowPaymentConfig({
            paymentProviderId: response.paymentProviderId || null,
            paymentCurrency: response.paymentCurrency || null,
            paymentEnabled: !!response.paymentEnabled,
          });

          // Check if backend found an existing draft for this user/request
          // If so, and we're not already on a draft page, redirect to the draft page
          if (response.draftId && !draftId) {
            // Navigate to the same page but with draft_id parameter
            // Preserve location state (e.g. isRequestActive) so the
            // destination page can still determine editability.
            navigate(
              `/customer/progress/requests/${requestId}?draft_id=${response.draftId}`,
              { replace: true, state: location.state }
            );
            return; // Stop processing - the component will re-mount with draftId
          }

          // If stageId is provided AND matches a submission stage, filter to that specific stage
          // If stageId doesn't match any submission stage (e.g., End stage for finished requests),
          // show ALL submission stages for complete view
          // show ALL submission stages for in progress under submission requests
          if (stageId && stages.length > 0) {
            const targetStage = stages.find((s) => s.id === stageId);
            // Only filter if target stage is found; otherwise keep all stages for finished requests
            // Only filter if target stage is found submission stage and request is active/assigned
            // Otherwise keep all stages for finished requests/inactive requests
            if (targetStage && isRequestActive) {
              stages = [targetStage];
            }
            // If targetStage not found, keep all stages (for view-only finished requests)
          } else if (!requestId && stages.length > 0) {
            // For new requests without requestId, default to first submission stage
            stages = [stages[0]];
          }

          // For existing requests, get subforms from the response if available
          // This ensures we show all filled subforms, not just those in stage config
          if (response.subforms?.length > 0) {
            // Store subforms for later use when mapping forms
            prefillData._subforms = response.subforms;
          }
        } else if (workflowVersionId) {
          const response = await getSubmissionStages(workflowVersionId);
          stages = response.stages || [];
          setWorkflowPaymentConfig({
            paymentProviderId: response.paymentProviderId || null,
            paymentCurrency: response.paymentCurrency || null,
            paymentEnabled: !!response.paymentEnabled,
          });
          // For new requests, only show the first submission stage
          if (stages.length > 0) {
            stages = [stages[0]];
          }
        }

        if (targetStageIds.length > 0) {
          stages = stages.filter((stage) => targetStageIds.includes(stage.id));
        }

        const mappedForms = stages.flatMap(mapStageToForms);

        // Filter prefillData to only include forms that belong to the CURRENT stage
        // This ensures first-time submissions show empty forms, while re-visits show pre-filled data
        const currentFormIds = mappedForms.map((f) => String(f.id));
        const filteredPrefillData = {};
        Object.keys(prefillData).forEach((key) => {
          if (currentFormIds.includes(key)) {
            filteredPrefillData[key] = prefillData[key];
          }
        });

        // Merge filtered prefill data with draft data and any existing filled forms
        // Priority (highest wins): (draftFormData > existingFilledForms > filteredPrefillData) if we're loading a draft
        // Note: Each form's data must be wrapped in { value: ... } format for parseFormData compatibility
        const formDataToUse = {
          ...filteredPrefillData,
        };

        // Merge existing filled forms (from server/previous submissions)
        Object.entries(existingFilledForms).forEach(
          ([formIdKey, formValue]) => {
            formDataToUse[formIdKey] = formValue;
          }
        );

        /**
         * Wrap draft form data in the expected { value: data } format.
         * This is applied LAST so draft data takes highest priority (user's unsaved changes).
         *
         * IMPORTANT: We MERGE with existing form data to preserve fileMetadata.
         * For sent-back drafts, the original server files should still be visible
         * for fields that weren't modified in the draft.
         *
         * Data structure:
         * - Original (from server): { value: "...", fileMetadata: [...] }
         * - Draft (overrides value): { ...original, value: "draft data" }
         */
        Object.entries(draftFormData).forEach(([formIdKey, formValue]) => {
          if (formValue && typeof formValue === 'object') {
            const wrappedData = { [formIdKey]: formValue };
            // MERGE with existing form data to preserve fileMetadata from original submission
            // This ensures untouched files from sent-back requests remain visible
            formDataToUse[formIdKey] = {
              ...formDataToUse[formIdKey], // Keep fileMetadata and other properties
              value: JSON.stringify(wrappedData), // Override with draft field values
            };
          }
        });

        dispatch({
          type: MULTI_FORM_ACTIONS.SET_FORMS,
          forms: mappedForms,
          formData: formDataToUse,
        });
      } catch (err) {
        console.error('Error fetching submission stages:', err);
        dispatch({ type: MULTI_FORM_ACTIONS.SET_ERROR, error: err.message });
      }
    };

    if (workflowVersionId || requestId) {
      fetchData();
    }
  }, [
    workflowVersionId,
    requestId,
    stageId,
    draftId,
    getDraftById,
    getSubmissionStages,
    getRequestSubmissionStages,
  ]);

  const handleFormTabChange = useCallback((_, value) => {
    if (value >= 0) {
      dispatch({ type: MULTI_FORM_ACTIONS.SET_ACTIVE_FORM, index: value });
    }
  }, []);

  const handleSubformNavigate = useCallback((index) => {
    dispatch({ type: MULTI_FORM_ACTIONS.SET_ACTIVE_SUBFORM, index });
  }, []);

  const handleSaveAllForms = useCallback(
    async (
      allFormData,
      deletedFileIds = [],
      additionalDocumentsComments = [],
      linkedRequestIds = [],
      stagedFileIds = [], // Legacy - deprecated
      fileData = [], // New file module - array of {id, formId, subformId, fieldId} objects
      draftIdToDelete = null,
      options = {}
    ) => {
      dispatch({
        type: MULTI_FORM_ACTIONS.SET_LOADING,
        loading: true,
        submitting: true,
      });

      try {
        const { returnResponse = false, showToast = true } = options;
        // Use stage_id (singular) with the first stage for new request creation
        // The backend only needs one stage_id to start the workflow
        const stageIds = state.forms.map((form) => form.stageId);
        const payload = {
          stage_id: stageIds[0],
          wf_version_id: workflowVersionId,
          ...(requestProgressId && { request_progress_id: requestProgressId }),
          ...(customRequestName && { study_name: customRequestName }),
          ...(additionalDocumentsComments && { additionalDocumentsComments }),
          ...(linkedRequestIds?.length > 0 && {
            linked_request_ids: linkedRequestIds,
          }),
        };

        const result = await submitFormData({
          allFormData,
          payload,
          deletedFileIds,
          stagedFileIds, // Legacy
          fileData, // New file module - includes metadata
          draftId: draftIdToDelete,
          returnResponse,
          showToast,
        });

        const wasSuccessful = returnResponse ? result.success : result;
        if (!wasSuccessful) {
          dispatch({
            type: MULTI_FORM_ACTIONS.SET_LOADING,
            loading: false,
            submitting: false,
          });
        }
        return result;
      } catch (err) {
        console.error('Error saving forms:', err);
        dispatch({ type: MULTI_FORM_ACTIONS.SET_ERROR, error: err.message });
        return false;
      }
    },
    [state.forms, workflowVersionId, requestProgressId, customRequestName]
  );

  const activeForm = state.forms[state.activeFormIndex] || null;
  const activeSubforms = activeForm?.subforms || [];

  return {
    forms: state.forms,
    activeFormIndex: state.activeFormIndex,
    activeSubformIndex: state.activeSubformIndex,
    activeForm,
    activeSubforms,
    formData: state.formData,
    loading: state.loading,
    submitting: state.submitting,
    error: state.error,
    hasMultipleForms: state.forms.length > 1,
    handleFormTabChange,
    handleSubformNavigate,
    handleSaveAllForms,
    draftStagedFiles,
    draftAdditionalDocumentsComments,
    draftLinkedRequestIds,
    workflowPaymentConfig,
  };
};

export default useMultiFormSubmission;

/**
 * ## Flow: Loading a Draft (New Request)
  1. URL: /submit/workflow/5?draft=123
  2. draftId = 123
  3. requestId = null (new request, no request_id)
  4. Load draft data:
    - draftFormData = { formId: { subformId: { fieldId: value } } }
    - draftStagedFiles = { "17-8-attachment": [{ id, name, size, ... }] }
  5. formData = draftFormData (direct copy)
  6. User continues from draft
  NOTE: File deletions are now immediate (no deferred deleted_file_ids).
 */

/**
   * ## Flow: Loading a Draft (Send-back)
  1. URL: /submit/workflow/5/request/10?draft=123
  2. draftId = 123
  3. requestId = 10 (existing request)
  4. Load existing filled forms:
    - existingFilledForms = { formId: { subformId: { fieldId: value } } }
  5. Load draft data:
    - draftFormData = { changes from draft }
    - draftStagedFiles = { staged files from draft }
  6. Merge: formData = deepMerge(existingFilledForms, draftFormData)
  7. Files: deleted files are already removed from attachments (immediate deletion)
  8. User continues from draft with existing data
  NOTE: File deletions are now immediate via dedicated endpoints:
    - Draft mode: PATCH /api/v1/drafts/:id/deleted-files
    - Non-draft mode: POST /api/endUserFilledForms/files/delete
   */
